import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Code, Target, CheckCircle, Type, Zap } from 'lucide-react';

import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const CRITERIA_TYPES = [
  {
    id: 'model_labels',
    name: 'Model Labels',
    description: 'Classification labels for evaluation',
    icon: Target,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    config: {
      labels: ['positive', 'negative', 'neutral'],
      multiSelect: false
    }
  },
  {
    id: 'model_scorer',
    name: 'Model Scorer',
    description: 'Numeric scoring within a specified range',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    config: {
      minScore: 0,
      maxScore: 10,
      description: 'Rate the response quality from 0 to 10'
    }
  },
  {
    id: 'python_grader',
    name: 'Python Grader',
    description: 'Custom Python evaluation logic',
    icon: Code,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    config: {
      code: `def evaluate(input_text, output_text, expected_output):
    """
    Custom evaluation function

    Args:
        input_text: The input prompt
        output_text: The model's response
        expected_output: The expected response

    Returns:
        dict: {'score': float, 'passed': bool, 'feedback': str}
    """
    # Your custom logic here
    score = 1.0 if output_text.strip() == expected_output.strip() else 0.0

    return {
        'score': score,
        'passed': score >= 0.5,
        'feedback': 'Exact match' if score == 1.0 else 'No match'
    }`
    }
  },
  {
    id: 'string_check',
    name: 'String Check',
    description: 'Exact or partial string matching criteria',
    icon: Type,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    config: {
      matchType: 'contains', // 'exact', 'contains', 'starts_with', 'ends_with'
      caseSensitive: false,
      expectedStrings: []
    }
  },
  {
    id: 'text_similarity',
    name: 'Text Similarity',
    description: 'Similarity threshold configuration',
    icon: CheckCircle,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    config: {
      threshold: 0.8,
      method: 'cosine', // 'cosine', 'jaccard', 'levenshtein'
      ignoreCase: true
    }
  }
];

const CriteriaDefinition = ({
  wizardData,
  currentStepData,
  onStepDataChange,
  onNext,
  onPrevious,
  saving
}) => {
  const [criteria, setCriteria] = useState(currentStepData?.criteria || []);
  const [selectedCriteriaType, setSelectedCriteriaType] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Prefill from template when template is selected
  const hasLoadedCriteriaRef = useRef(false);
  const previousTemplateIdRef = useRef(null);

  useEffect(() => {
    try {
      const selectedTemplate = wizardData?.template?.template;

      // Early return if no template
      if (!selectedTemplate || typeof selectedTemplate !== 'object') {
        return;
      }

      const currentTemplateId = selectedTemplate.id;
      const templateData = selectedTemplate.template_data;

      // Early return if no template data or criteria
      if (!templateData || !Array.isArray(templateData.criteria) || templateData.criteria.length === 0) {
        return;
      }

      // Check if template changed or criteria is empty
      const templateChanged = previousTemplateIdRef.current !== currentTemplateId;
      const shouldPrefill = (criteria.length === 0 || templateChanged) && !hasLoadedCriteriaRef.current;

      if (shouldPrefill) {
        // Validate criteria structure before setting
        const validCriteria = templateData.criteria.filter(criterion =>
          criterion &&
          typeof criterion === 'object' &&
          criterion.name &&
          criterion.type
        );

        if (validCriteria.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('CriteriaDefinition: Prefilling criteria from template:', validCriteria);
          }

          setCriteria(validCriteria);
          hasLoadedCriteriaRef.current = true;
          previousTemplateIdRef.current = currentTemplateId;

          toast.success(`${validCriteria.length} ${validCriteria.length === 1 ? 'criterion' : 'criteria'} loaded from template`);
        }
      }
    } catch (error) {
      console.error('Error loading criteria from template:', error);
      toast.error('Failed to load criteria from template');
    }
  }, [wizardData?.template?.template, wizardData?.template?.template?.id, criteria.length, setCriteria]);

  // Reset loaded flag when navigating away and back
  useEffect(() => {
    return () => {
      hasLoadedCriteriaRef.current = false;
    };
  }, []);

  // Update step data when criteria changes
  useEffect(() => {
    onStepDataChange({ criteria });
  }, [criteria, onStepDataChange]);

  // Validate criteria
  const validateCriteria = () => {
    const errors = {};

    if (criteria.length === 0) {
      errors.criteria = 'At least one criterion is required';
    }

    criteria.forEach((criterion, index) => {
      if (!criterion.name?.trim()) {
        errors[`criterion_${index}_name`] = 'Criterion name is required';
      }

      // Validate specific criterion configurations
      if (criterion.type === 'model_labels' && (!criterion.config.labels || criterion.config.labels.length === 0)) {
        errors[`criterion_${index}_labels`] = 'At least one label is required';
      }

      if (criterion.type === 'model_scorer') {
        if (criterion.config.minScore >= criterion.config.maxScore) {
          errors[`criterion_${index}_range`] = 'Min score must be less than max score';
        }
      }

      if (criterion.type === 'python_grader' && !criterion.config.code?.trim()) {
        errors[`criterion_${index}_code`] = 'Python code is required';
      }

      if (criterion.type === 'string_check' && (!criterion.config.expectedStrings || criterion.config.expectedStrings.length === 0)) {
        errors[`criterion_${index}_strings`] = 'At least one expected string is required';
      }

      if (criterion.type === 'text_similarity' && (criterion.config.threshold < 0 || criterion.config.threshold > 1)) {
        errors[`criterion_${index}_threshold`] = 'Threshold must be between 0 and 1';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }; // Add new criterion
  const addCriterion = (criteriaType) => {
    const newCriterion = {
      id: Date.now().toString(),
      type: criteriaType.id,
      name: `${criteriaType.name} ${criteria.length + 1}`,
      weight: 1.0,
      config: { ...criteriaType.config }
    };

    setCriteria(prev => [...prev, newCriterion]);
    setSelectedCriteriaType(null);

    // Scroll to bottom to show new criterion
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 100);
  };

  // Remove criterion
  const removeCriterion = (index) => {
    setCriteria(prev => prev.filter((_, i) => i !== index));
  };

  // Update criterion
  const updateCriterion = (index, field, value) => {
    setCriteria(prev => prev.map((criterion, i) =>
      i === index ? { ...criterion, [field]: value } : criterion
    ));
  };

  // Update criterion config
  const updateCriterionConfig = (index, configField, value) => {
    setCriteria(prev => prev.map((criterion, i) =>
      i === index
        ? {
          ...criterion,
          config: { ...criterion.config, [configField]: value }
        }
        : criterion
    ));
  };

  // Handle next step
  const handleNext = () => {
    if (validateCriteria()) {
      onNext();
    } else {
      toast.error('Please fix the validation errors before proceeding');
    }
  };

  // Render criterion configuration
  const renderCriterionConfig = (criterion, index) => {
    switch (criterion.type) {
      case 'model_labels':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Labels *
              </label>
              <div className="space-y-2">
                {criterion.config.labels.map((label, labelIndex) => (
                  <div key={labelIndex} className="flex items-center space-x-2">
                    <Input
                      value={label}
                      onChange={(e) => {
                        const newLabels = [...criterion.config.labels];
                        newLabels[labelIndex] = e.target.value;
                        updateCriterionConfig(index, 'labels', newLabels);
                      }}
                      placeholder="Enter label"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newLabels = criterion.config.labels.filter((_, i) => i !== labelIndex);
                        updateCriterionConfig(index, 'labels', newLabels);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateCriterionConfig(index, 'labels', [...criterion.config.labels, '']);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Label
                </Button>
              </div>
              {validationErrors[`criterion_${index}_labels`] && (
                <p className="text-red-500 text-sm mt-1">{validationErrors[`criterion_${index}_labels`]}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={criterion.config.multiSelect}
                onChange={(e) => updateCriterionConfig(index, 'multiSelect', e.target.checked)}
                className="rounded"
              />
              <label className="text-sm text-gray-700">Allow multiple selections</label>
            </div>
          </div>
        );

      case 'model_scorer':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Score
                </label>
                <Input
                  type="number"
                  value={criterion.config.minScore}
                  onChange={(e) => updateCriterionConfig(index, 'minScore', parseFloat(e.target.value))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Score
                </label>
                <Input
                  type="number"
                  value={criterion.config.maxScore}
                  onChange={(e) => updateCriterionConfig(index, 'maxScore', parseFloat(e.target.value))}
                />
              </div>
            </div>
            {validationErrors[`criterion_${index}_range`] && (
              <p className="text-red-500 text-sm">{validationErrors[`criterion_${index}_range`]}</p>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={criterion.config.description}
                onChange={(e) => updateCriterionConfig(index, 'description', e.target.value)}
                placeholder="Describe what this score represents..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'python_grader':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Python Code *
            </label>
            <textarea
              value={criterion.config.code}
              onChange={(e) => updateCriterionConfig(index, 'code', e.target.value)}
              placeholder="Enter your Python evaluation code..."
              rows={12}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${validationErrors[`criterion_${index}_code`] ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {validationErrors[`criterion_${index}_code`] && (
              <p className="text-red-500 text-sm mt-1">{validationErrors[`criterion_${index}_code`]}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Function must return a dict with 'score', 'passed', and 'feedback' keys.
            </p>
          </div>
        );

      case 'string_check':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Match Type
              </label>
              <Select
                value={criterion.config.matchType}
                onValueChange={(value) => updateCriterionConfig(index, 'matchType', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact Match</SelectItem>
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="starts_with">Starts With</SelectItem>
                  <SelectItem value="ends_with">Ends With</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={criterion.config.caseSensitive}
                onChange={(e) => updateCriterionConfig(index, 'caseSensitive', e.target.checked)}
                className="rounded"
              />
              <label className="text-sm text-gray-700">Case sensitive</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Strings *
              </label>
              <div className="space-y-2">
                {criterion.config.expectedStrings.map((str, strIndex) => (
                  <div key={strIndex} className="flex items-center space-x-2">
                    <Input
                      value={str}
                      onChange={(e) => {
                        const newStrings = [...criterion.config.expectedStrings];
                        newStrings[strIndex] = e.target.value;
                        updateCriterionConfig(index, 'expectedStrings', newStrings);
                      }}
                      placeholder="Enter expected string"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newStrings = criterion.config.expectedStrings.filter((_, i) => i !== strIndex);
                        updateCriterionConfig(index, 'expectedStrings', newStrings);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateCriterionConfig(index, 'expectedStrings', [...criterion.config.expectedStrings, '']);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add String
                </Button>
              </div>
              {validationErrors[`criterion_${index}_strings`] && (
                <p className="text-red-500 text-sm mt-1">{validationErrors[`criterion_${index}_strings`]}</p>
              )}
            </div>
          </div>
        );

      case 'text_similarity':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Similarity Method
              </label>
              <Select
                value={criterion.config.method}
                onValueChange={(value) => updateCriterionConfig(index, 'method', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cosine">Cosine Similarity</SelectItem>
                  <SelectItem value="jaccard">Jaccard Similarity</SelectItem>
                  <SelectItem value="levenshtein">Levenshtein Distance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Threshold: {criterion.config.threshold}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={criterion.config.threshold}
                onChange={(e) => updateCriterionConfig(index, 'threshold', parseFloat(e.target.value))}
                className="w-full"
              />
              {validationErrors[`criterion_${index}_threshold`] && (
                <p className="text-red-500 text-sm mt-1">{validationErrors[`criterion_${index}_threshold`]}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={criterion.config.ignoreCase}
                onChange={(e) => updateCriterionConfig(index, 'ignoreCase', e.target.checked)}
                className="rounded"
              />
              <label className="text-sm text-gray-700">Ignore case</label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Create Test Criteria</h2>
        <p className="text-gray-600">
          Define how your evaluation results will be scored and validated.
        </p>
      </div>

      {/* Criteria Type Selection */}
      {!selectedCriteriaType && (
        <div>
          <h3 className="text-lg font-medium mb-4">Add New Criterion</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CRITERIA_TYPES.map(criteriaType => {
              const Icon = criteriaType.icon;
              return (
                <Card
                  key={criteriaType.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => addCriterion(criteriaType)}
                >
                  <CardHeader className="pb-3">
                    <div className={`w-10 h-10 rounded-lg ${criteriaType.bgColor} flex items-center justify-center mb-3`}>
                      <Icon className={`h-5 w-5 ${criteriaType.color}`} />
                    </div>
                    <CardTitle className="text-base">{criteriaType.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {criteriaType.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Existing Criteria */}
      {criteria.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Evaluation Criteria</h3>
            {validationErrors.criteria && (
              <p className="text-red-500 text-sm">{validationErrors.criteria}</p>
            )}
          </div>

          <div className="space-y-4">
            {criteria.map((criterion, index) => {
              const criteriaType = CRITERIA_TYPES.find(type => type.id === criterion.type);
              const Icon = criteriaType?.icon || Target;

              return (
                <Card key={criterion.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg ${criteriaType?.bgColor || 'bg-gray-50'} flex items-center justify-center`}>
                          <Icon className={`h-4 w-4 ${criteriaType?.color || 'text-gray-600'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{criteriaType?.name || criterion.type}</CardTitle>
                          <CardDescription className="text-sm">
                            Weight: {criterion.weight}
                          </CardDescription>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCriterion(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Criterion Name *
                        </label>
                        <Input
                          value={criterion.name}
                          onChange={(e) => updateCriterion(index, 'name', e.target.value)}
                          placeholder="Enter criterion name"
                          className={validationErrors[`criterion_${index}_name`] ? 'border-red-500' : ''}
                        />
                        {validationErrors[`criterion_${index}_name`] && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors[`criterion_${index}_name`]}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Weight
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          value={criterion.weight}
                          onChange={(e) => updateCriterion(index, 'weight', parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    {renderCriterionConfig(criterion, index)}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {criteria.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No evaluation criteria defined yet</p>
          <p className="text-sm text-gray-400 mb-6">
            Add criteria to define how your evaluation results will be scored
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>

        <Button
          onClick={handleNext}
          disabled={saving || criteria.length === 0}
        >
          {saving ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};

export default CriteriaDefinition;
